import type {ReactElement} from 'react';
import React from 'react';
import {TextItemBaseBackgroundOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-item-base-background-option';
import type {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromBackgroundOfTextSlideItem} from '@Hooks/poster-editor/useGetKeyFromBackgroundOfTextSlideItem';
import {usegetKeyOfFillOfBackgroundOfTextSlideItem} from '@Hooks/poster-editor/usegetKeyOfFillOfBackgroundOfTextSlideItem';
import type {RGBA} from '@Utils/color.util';

interface TextSlideBackgroundOptionProps {
  onClickColorFill(): void;
}

export function TextSlideBackgroundOption({...props}: TextSlideBackgroundOptionProps): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const rx = useGetKeyFromBackgroundOfTextSlideItem('rx');
  const ry = useGetKeyFromBackgroundOfTextSlideItem('ry');
  const fillType = usegetKeyOfFillOfBackgroundOfTextSlideItem('fillType');
  const fillColor = usegetKeyOfFillOfBackgroundOfTextSlideItem('fillColor');

  if (!activeItem?.isSlideshow()) {
    throw new Error(`Expected slideshow item but got ${activeItem?.gitype}`);
  }

  if (!activeSlide?.isTextSlide()) {
    throw new Error(`Expected text slideshow item but got ${activeSlide?.gitype}`);
  }

  if (!activeSlideUID) {
    throw new Error('unable to get the selected text slide');
  }

  if (rx === undefined || ry === undefined) {
    throw new Error('unable to get the background properties of selected text slide');
  }

  if (fillType === undefined || !fillColor) {
    throw new Error('unable to get the fill properties of background of selected text slide');
  }

  const onChangeFillType = (newType: FillTypes): void => {
    void activeItem.updateSlideFromObject(activeSlideUID, {
      background: {
        fill: {
          fillType: newType,
          fillColor: activeSlide.background.fill.getColorForNewType(newType),
        },
      },
    });
  };

  const updateOpacity = (fillAlpha: number, undoable = true): void => {
    const slideItemFillColor = fillColor.slice();
    for (let i = 0; i < fillColor.length; i++) {
      const color = fillColor[i].slice();
      color[3] = fillAlpha;
      slideItemFillColor[i] = color as RGBA;
    }
    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        background: {
          fill: {
            fillColor: slideItemFillColor,
          },
        },
      },
      {
        undoable,
      }
    );
  };

  const updateRoundness = (val: number, undoable = true): void => {
    void activeItem.updateSlideFromObject(
      activeSlideUID,
      {
        background: {
          rx: val,
          ry: val,
        },
      },
      {
        undoable,
      }
    );
  };

  return (
    <TextItemBaseBackgroundOption
      background={{
        fill: {
          fillType,
          fillColor,
        },
        rx,
        ry,
      }}
      onChangeFillType={onChangeFillType}
      onUpdateOpacity={updateOpacity}
      onUpdateRoundness={updateRoundness}
      onClickColorFill={(): void => {
        props.onClickColorFill();
      }}
    />
  );
}
